import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, Mail, Phone, Lock, ArrowLeft, Save, Gift, ChevronDown } from 'lucide-react';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import { Notification } from '../types';
import { isPlatformSystemAdmin } from '../utils/roleUtils';
import { formatPhoneNumber, US_DISPLAY_PHONE_PATTERN } from '../utils/phoneFormatUtils';
import toast from 'react-hot-toast';

const CustomerProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const platformContext =
    location.pathname.startsWith('/dashboard/platform') ||
    (!!user && isPlatformSystemAdmin(user));
  const dashboardHome = platformContext ? '/dashboard/platform' : '/dashboard/customer';
  /** Platform admin profile uses dedicated route; omit customer-only fields. */
  const isPlatformProfilePage = location.pathname.startsWith('/dashboard/platform/profile');

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(
    formatPhoneNumber(
      String(user?.phoneNumber ?? user?.phone ?? (user as { phone_number?: string })?.phone_number ?? '')
    )
  );
  const [birthMonth, setBirthMonth] = useState<number | string | undefined>(user?.birthMonth ? String(user.birthMonth) : '');
  const [birthDay, setBirthDay] = useState<number | string | undefined>(user?.birthDay ? String(user.birthDay) : '');
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  
  // Update birthday fields when user changes
  useEffect(() => {
    if (user) {
      if (user.birthMonth !== undefined && user.birthMonth !== null) {
        setBirthMonth(String(user.birthMonth));
      } else {
        setBirthMonth('');
      }
      if (user.birthDay !== undefined && user.birthDay !== null) {
        setBirthDay(String(user.birthDay));
      } else {
        setBirthDay('');
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const raw = user.phoneNumber ?? user.phone ?? (user as { phone_number?: string }).phone_number ?? '';
    setPhoneNumber(formatPhoneNumber(String(raw)));
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPhone = phoneNumber.trim();
    if (!isPlatformProfilePage && !US_DISPLAY_PHONE_PATTERN.test(trimmedPhone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setSaving(true);
    try {
      // Convert birthday fields to numbers or null
      const birthMonthValue = birthMonth === '' || birthMonth === undefined ? null : (typeof birthMonth === 'string' ? parseInt(birthMonth, 10) : birthMonth);
      const birthDayValue = birthDay === '' || birthDay === undefined ? null : (typeof birthDay === 'string' ? parseInt(birthDay, 10) : birthDay);
      
      const updated = await apiService.updateCurrentUser(
        isPlatformProfilePage
          ? { firstName, lastName, email }
          : {
              firstName,
              lastName,
              email,
              phoneNumber: trimmedPhone,
              phone: trimmedPhone,
              birthMonth: birthMonthValue || undefined,
              birthDay: birthDayValue || undefined,
            }
      );
      // Preserve roles and other important fields that might not be returned by the API
      const updatedUserWithRoles = {
        ...updated,
        roles: updated.roles || user?.roles || [],
        canPerformServices: updated.canPerformServices ?? user?.canPerformServices,
        googleId: updated.googleId ?? user?.googleId ?? (user as { google_id?: string }).google_id
      };
      updateUser(updatedUserWithRoles);
      toast.success('Profile updated successfully');
      // Redirect to customer dashboard after successful save
      navigate(dashboardHome);
    } catch (err: any) {
      console.error('Failed to update profile', err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Load notifications for navbar
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        const unread = notifications.filter((n: Notification) => !n.is_read);
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  if (!user) return null;

  const isGoogleLinkedAccount = Boolean(
    user.googleId ?? (user as { google_id?: string | null }).google_id
  );

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar 
        showDashboardLink={true} 
        showAuthButtons={false}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate(platformContext ? dashboardHome : '/dashboard/customer/notifications')}
      />

      <div className="flex-1 pt-6 pb-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {/* Breadcrumb Navigation */}
          <div className="mb-4">
            <Link
              to={dashboardHome}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
            </div>

          {/* Page Header */}
          <div className="mb-4">
            <h1 className="text-xl font-bold mb-0">Account Settings</h1>
            <p className="text-gray-600 text-sm mb-0">Manage your personal information and account security</p>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-50 rounded-lg mr-3">
                    <User className="w-5 h-5 text-blue-900" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
                    <p className="text-sm text-gray-500">Update your personal details and contact information</p>
                  </div>
                </div>
              </div>
              
              <form ref={formRef} onSubmit={handleSave} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                  <input
                      id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                      className="aacore-textfield-primary"
                      placeholder="Enter your first name"
                    required
                  />
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                  <input
                      id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                      className="aacore-textfield-primary"
                      placeholder="Enter your last name"
                    required
                  />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                  <input
                        id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                        className="aacore-textfield-primary pl-10"
                        placeholder="your.email@example.com"
                    required
                  />
                    </div>
                  </div>
                  
                  {!isPlatformProfilePage && (
                  <>
                  <div className="md:col-span-2">
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                  <input
                        id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                        className="aacore-textfield-primary pl-10"
                    placeholder="(555) 123-4567"
                    required
                  />
                  </div>
                  </div>

                  {/* Birthday Field (optional) */}
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="birthMonth" className="block text-sm font-medium text-gray-700">
                        Birthday <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(11, 71, 120, 0.1)', color: '#0B4778' }}>
                        <Gift className="h-3 w-3" />
                        Month & day only (no year)
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">Many businesses offer special birthday discounts. Share your month and day if you'd like.</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="birthMonth" className="block text-sm font-medium text-gray-700 mb-2">
                          Birth month
                        </label>
                        <div className="relative">
                          <select
                            id="birthMonth"
                            className="aacore-textfield-primary appearance-none pr-10"
                            value={birthMonth !== undefined && birthMonth !== null && birthMonth !== '' ? String(birthMonth) : ''}
                            onChange={(e) => {
                              const monthValue = e.target.value || '';
                              setBirthMonth(monthValue);
                              // If month changes and day is invalid for new month, clear day
                              if (monthValue && birthDay) {
                                const monthNum = parseInt(monthValue, 10);
                                const dayNum = typeof birthDay === 'string' ? parseInt(birthDay, 10) : birthDay;
                                if (monthNum && dayNum) {
                                  const daysInMonth = new Date(2000, monthNum, 0).getDate();
                                  if (dayNum > daysInMonth) {
                                    setBirthDay('');
                                    toast.error(`Selected month only has ${daysInMonth} days. Please reselect a day.`);
                                  }
                                }
                              }
                            }}
                          >
                            <option value="">Select month</option>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                              const date = new Date(2000, month - 1, 1);
                              const monthName = date.toLocaleString('default', { month: 'long' });
                              return (
                                <option key={month} value={month}>
                                  {monthName}
                                </option>
                              );
                            })}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="birthDay" className="block text-sm font-medium text-gray-700 mb-2">
                          Birth day
                        </label>
                        <div className="relative">
                          <select
                            id="birthDay"
                            className="aacore-textfield-primary appearance-none pr-10"
                            value={birthDay !== undefined && birthDay !== null && birthDay !== '' ? String(birthDay) : ''}
                            onChange={(e) => {
                              const dayValue = e.target.value || '';
                              setBirthDay(dayValue);
                              // Validate day is valid for selected month
                              if (dayValue && birthMonth) {
                                const monthNum = typeof birthMonth === 'string' ? parseInt(birthMonth, 10) : birthMonth;
                                const dayNum = parseInt(dayValue, 10);
                                if (monthNum) {
                                  const daysInMonth = new Date(2000, monthNum, 0).getDate();
                                  if (dayNum > daysInMonth) {
                                    toast.error(`Selected month only has ${daysInMonth} days`);
                                    setBirthDay('');
                                  }
                                }
                              }
                            }}
                            disabled={!birthMonth}
                          >
                            <option value="">Select day</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>
                  </div>
                  </>
                  )}
              </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  onClick={() => formRef.current?.requestSubmit()}
                  disabled={saving}
                  className="aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                </button>
                </div>
              </form>
            </div>

            {!isGoogleLinkedAccount && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg mr-3">
                    <Lock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Security</h2>
                    <p className="text-sm text-gray-500">Manage your password and account security</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between py-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Password</h3>
                    <p className="text-sm text-gray-500 mt-1">Last updated: {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'Never'}</p>
                  </div>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(true)}
                  className="aacore-btn-danger ml-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                    <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {showPasswordModal && !isGoogleLinkedAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg mr-3">
                    <Lock className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(false)} 
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!currentPassword || !newPassword) {
                  toast.error('Please fill out all fields');
                  return;
                }
                if (newPassword !== confirmPassword) {
                  toast.error('New passwords do not match');
                  return;
                }
                setChangingPassword(true);
                try {
                  await apiService.changePassword(currentPassword, newPassword);
                  toast.success('Password changed successfully');
                  setShowPasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                } catch (err: any) {
                  console.error('Change password error', err);
                  toast.error(err?.response?.data?.message || 'Failed to change password');
                } finally {
                  setChangingPassword(false);
                }
              }}
              className="p-6 space-y-5"
            >
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="aacore-textfield-primary"
                  placeholder="Enter your current password"
                  required
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="aacore-textfield-primary"
                  placeholder="Enter your new password"
                  required
                  minLength={8}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Password must be at least 8 characters and include uppercase, lowercase, and a number.
                </p>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="aacore-textfield-primary"
                  placeholder="Confirm your new password"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="aacore-btn-outlined focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                  disabled={changingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                  {changingPassword ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CustomerProfilePage;
